// Upload content-type sniffing. A declared MIME and extension are attacker-controlled;
// a file's leading magic bytes are not. The media upload route checks content against
// the declared MIME for every type fingerprinted here.

/** One byte pattern at a fixed offset. */
type Part = { offset: number; bytes: readonly number[] }
/** A signature matches when ALL of its parts match. */
type Signature = readonly Part[]

const ascii = (s: string) => [...s].map((c) => c.charCodeAt(0))

/** Magic-byte signatures per MIME. A file matches when ANY signature matches. */
const SIGNATURES: Readonly<Record<string, readonly Signature[]>> = {
  'application/pdf': [[{ offset: 0, bytes: ascii('%PDF') }]],
  'image/jpeg': [[{ offset: 0, bytes: [0xff, 0xd8, 0xff] }]], // JFIF/EXIF share this prefix
  'image/png': [[{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }]],
  // RIFF container whose form type is WEBP (a WAV renamed to .webp no longer passes).
  'image/webp': [[{ offset: 0, bytes: ascii('RIFF') }, { offset: 8, bytes: ascii('WEBP') }]],
  // ISO-BMFF `ftyp` box with an AVIF major brand (still image or sequence).
  'image/avif': [
    [{ offset: 4, bytes: ascii('ftypavif') }],
    [{ offset: 4, bytes: ascii('ftypavis') }],
  ],
}

const partMatches = (bytes: Uint8Array, part: Part) =>
  bytes.length >= part.offset + part.bytes.length && part.bytes.every((b, i) => bytes[part.offset + i] === b)

/**
 * Do the file's leading bytes match its DECLARED MIME type?
 *
 * - For a fingerprinted MIME: `true` only if a signature matches, so spoofed content fails.
 * - For a MIME without a signature (e.g. SVG, text): `true`. The caller's MIME allowlist
 *   is still the gate for which types are accepted at all.
 *
 * Pass the head of the file (the first 16 bytes cover every signature here).
 */
export function contentMatchesDeclaredMime(bytes: Uint8Array, mime: string): boolean {
  const signatures = SIGNATURES[mime]
  if (!signatures) return true
  return signatures.some((sig) => sig.every((part) => partMatches(bytes, part)))
}
